import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    OnChanges,
    ViewChild,
    SimpleChanges,
    input,
    model,
    output,
} from '@angular/core';
import {
    IActionMapping,
    ITreeOptions,
    ITreeState,
    TREE_ACTIONS,
    TreeModel,
    TreeNode,
} from '@ali-hm/angular-tree-component';

export interface KuiTreeSelectNode {
    id: string;
    name: string;
    icon?: string;
    color?: string;
    children?: KuiTreeSelectNode[];
    hasChildren?: boolean;
    getChildren?: (node: KuiTreeSelectNode) => Promise<KuiTreeSelectNode[]>;
    node?: {
        realParent?: {
            [property: string]: any;
        };
        data: {
            [property: string]: any;
        };
    };
    eventName?: string;
    data?: any;
    active?: boolean;
}

const singleSelectActionMap: IActionMapping = {
    mouse: {
        click(tree, node, $event) {
            if (node.hasChildren) {
                TREE_ACTIONS.TOGGLE_EXPANDED(tree, node, $event);
            } else {
                TREE_ACTIONS.TOGGLE_ACTIVE(tree, node, $event);
            }
        },
    },
};

@Component({
    selector: 'kui-tree-select',
    templateUrl: 'tree-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KuiTreeSelectComponent implements AfterViewInit, OnChanges {
    singleSelect = input<boolean>(false);
    expandRootOnly = input<boolean>(false);

    nodes = input<KuiTreeSelectNode[]>([]);
    updatedNodes: KuiTreeSelectNode[] = [];
    selectedNodeChanged = output<KuiTreeSelectNode>();

    state = model<ITreeState>(undefined);
    stateChange = output<ITreeState>();

    selectedChange = output<string>();
    selectedNodeId: string;
    selected = model<string>(undefined);

    @ViewChild('tree') tree: any;

    treeModel: TreeModel;
    options: ITreeOptions = {
        getChildren: (node: TreeNode): Promise<KuiTreeSelectNode[]> => {
            if (node.data.getChildren) {
                return node.data.getChildren(node.data);
            }
            return Promise.resolve([]);
        },
    };

    ngAfterViewInit() {
        this.treeModel = this.tree.treeModel;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['singleSelect'] && this.singleSelect()) {
            this.options.actionMapping = singleSelectActionMap;
        }

        if (changes['nodes']) {
            const value = this.nodes();
            this.updatedNodes = this.normalizeTree(value);
            if (Array.isArray(value) && value.length === 1) {
                setTimeout(() => {
                    if (this.expandRootOnly()) {
                        //Just expand the main root item, everything else should be collapsed by default.
                        this.treeModel?.roots[0]?.expand();
                    } else {
                        this.treeModel?.expandAll();
                    }
                });
            }
            if (this.selectedNodeId) {
                setTimeout(() => {
                    const node = this.treeModel?.getNodeById(this.selectedNodeId);
                    if (node) { node.setActiveAndVisible(); }
                });
            }
        }

        if (changes['state']) {
            const value = this.state();
            this.stateChange.emit(value);
            this.selectedChange.emit(this.selected());
        }

        if (changes['selected']) {
            const value = this.selected();
            this.selectedNodeId = value;
            setTimeout(() => {
                if (this.treeModel && value) {
                    const node = this.treeModel.getNodeById(value);
                    if (node) { node.setActiveAndVisible(); }
                }
            });
        }
    }

    clearSelection() {
        this.treeModel.setFocus(false);
        this.treeModel.collapseAll();

        this.treeModel.setState({
            selectedNodeIds: [],
            activeNodeIds: [],
            expandedNodeIds: [],
            selectedLeafNodeIds: [],
            focusedNodeId: null,
        });
    }

    onToggle(node: any, event: MouseEvent) {
        if (!node.hasChildren) return;

        // if inside a popup/dropdown menu this prevents it from closing it
        // when clicking the toggle icon
        event.stopPropagation();
        node.toggleExpanded();
    }

    normalizeTree(nodes: any[]): any[] {
        if (!Array.isArray(nodes)) return [];
      
        return nodes.map(node => this.normalizeNode(node));
    }
      
    private normalizeNode(node: any): any {
        if (!Array.isArray(node.children)) return node;
        
        return {
            ...node,
            children: node.children.map(child => this.normalizeNode(child))
        };
    }
      
}
